import * as Sentry from "@sentry/browser";
import _ from "lodash";
import moment from "moment";
import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { makeId, User, validateVenue } from "sweet-global";
import { Venue, UserVenue } from "sweet-global";
import firebase from "sweet-web/firebase";
import * as selectors from "sweet-web/redux/selectors";
import { UpdateUser } from "sweet-web/redux/user/actions";
import { RootState } from "../index";
import {
  CREATE_VENUE_FAILURE,
  CREATE_VENUE_INIT,
  CREATE_VENUE_SUCCESS,
  DELETE_VENUE_FAILURE,
  DELETE_VENUE_INIT,
  DELETE_VENUE_SUCCESS,
  GET_MY_VENUES_FAILURE,
  GET_MY_VENUES_INIT,
  GET_MY_VENUES_SUCCESS,
  GET_VENUES_FAILURE,
  GET_VENUES_INIT,
  GET_VENUES_SUCCESS,
  LINK_STRIPE,
  RESET_VENUES,
  UPDATE_VENUE_FAILURE,
  UPDATE_VENUE_INIT,
  UPDATE_VENUE_SUCCESS,
  VenuesActionTypes
} from "./types";

const firestore = firebase.firestore();
const storage = firebase.storage();

export const resetVenues = (): VenuesActionTypes => {
  return { type: RESET_VENUES };
};

export const getVenuesInit = (): VenuesActionTypes => {
  return { type: GET_VENUES_INIT };
};

export const getVenuesSuccess = (
  venues: {
    [venueId: string]: Venue;
  },
  venueUsers: {
    [venueId: string]: { [userId: string]: User };
  }
): VenuesActionTypes => {
  return { type: GET_VENUES_SUCCESS, venues, venueUsers };
};

export const getVenuesFailure = (error: Error): VenuesActionTypes => {
  return { type: GET_VENUES_FAILURE, error };
};

export const getMyVenuesInit = (): VenuesActionTypes => {
  return { type: GET_MY_VENUES_INIT };
};

export const getMyVenuesSuccess = (
  venues: {
    [venueId: string]: Venue;
  },
  venueUsers: {
    [venueId: string]: { [userId: string]: User };
  }
): VenuesActionTypes => {
  return { type: GET_MY_VENUES_SUCCESS, venues, venueUsers };
};

export const getMyVenuesFailure = (error: Error): VenuesActionTypes => {
  return { type: GET_MY_VENUES_FAILURE, error };
};

export const createVenueInit = (): VenuesActionTypes => {
  return { type: CREATE_VENUE_INIT };
};

export const createVenueSuccess = (
  venueId: string,
  venueEntity: Venue
): VenuesActionTypes => {
  return { type: CREATE_VENUE_SUCCESS, venueId, venueEntity };
};

export const createVenueFailure = (error: Error): VenuesActionTypes => {
  return { type: CREATE_VENUE_FAILURE, error };
};

export const updateVenueInit = (): VenuesActionTypes => {
  return { type: UPDATE_VENUE_INIT };
};

export const updateVenueSuccess = (
  venueId: string,
  venue: Venue
): VenuesActionTypes => {
  return { type: UPDATE_VENUE_SUCCESS, venueId, venue };
};

export const updateVenueFailure = (error: Error): VenuesActionTypes => {
  return { type: UPDATE_VENUE_FAILURE, error };
};

export const deleteVenueInit = (): VenuesActionTypes => {
  return { type: DELETE_VENUE_INIT };
};

export const deleteVenueSuccess = (venueId: string): VenuesActionTypes => {
  return { type: DELETE_VENUE_SUCCESS, venueId };
};

export const deleteVenueFailure = (error: Error): VenuesActionTypes => {
  return { type: DELETE_VENUE_FAILURE, error };
};

export const linkStripe = (state: string): VenuesActionTypes => {
  return { type: LINK_STRIPE, state };
};

export const GetMyVenues = (): ThunkAction<
  Promise<void>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(getMyVenuesInit());

    const state = getState();
    const uid = selectors.uid(state);
    const userType = selectors.userType(state);

    if (!uid) throw new Error("no authorized user");

    let venuesQuery: firebase.firestore.Query = null;
    if (userType === "VENUE") {
      venuesQuery = firestore
        .collection("venues")
        .where(`members.${uid}.active`, "==", true);
    } else if (userType === "ADMIN") {
      venuesQuery = firestore.collection("venues");
    }

    const querySnap = await venuesQuery.get();

    const venues: { [venueId: string]: Venue } = {};
    const venueUsers: { [venueId: string]: { [userId: string]: User } } = {};

    for (const doc of querySnap.docs) {
      venues[doc.id] = {
        id: doc.id,
        ...doc.data()
      } as Venue;

      // get members of venue
      if (typeof venues[doc.id].members === "object") {
        for (const venueMemberId of Object.keys(venues[doc.id].members)) {
          const venueMemberDoc = await firestore
            .collection("users")
            .doc(venueMemberId)
            .get();
          if (!venueUsers[doc.id]) venueUsers[doc.id] = {};
          venueUsers[doc.id][venueMemberId] = {
            id: venueMemberId,
            ...venueMemberDoc.data()
          } as User;
        }
      }
    }

    dispatch(getMyVenuesSuccess(venues, venueUsers));
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(getMyVenuesFailure(error));
  }
};

export const GetVenues = (): ThunkAction<
  Promise<void>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(getVenuesInit());

    const venuesQuery = await firestore
      .collection("venues")
      .where("active", "==", true)
      .get();

    const venues: { [venueId: string]: Venue } = {};
    const venueUsers: { [venueId: string]: { [userId: string]: User } } = {};

    for (const doc of venuesQuery.docs) {
      venues[doc.id] = {
        id: doc.id,
        ...doc.data()
      } as Venue;

      // get members of venue
      if (typeof venues[doc.id].members === "object") {
        for (const venueMemberId of Object.keys(venues[doc.id].members)) {
          const venueMemberDoc = await firestore
            .collection("users")
            .doc(venueMemberId)
            .get();
          if (!venueUsers[doc.id]) venueUsers[doc.id] = {};
          venueUsers[doc.id][venueMemberId] = {
            id: venueMemberId,
            ...venueMemberDoc.data()
          } as User;
        }
      }
    }

    dispatch(getVenuesSuccess(venues, venueUsers));
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(getVenuesFailure(error));
  }
};

export const CreateVenue = (): ThunkAction<
  Promise<string>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(createVenueInit());

    const state = getState();
    const uid = selectors.uid(state);
    const userType = selectors.userType(state);

    if (!uid) throw new Error("no authorized user");
    if (userType !== "VENUE" && userType !== "ADMIN")
      throw new Error("wrong user type");

    const venue: Venue = {
      name: "",
      website: "",
      phoneNumber: "",
      emailAddress: "",
      location: {
        address: "",
        city: "",
        country: "",
        latitude: null,
        longitude: null
      },
      description: "",
      eventTypes: [],
      options: {},
      photos: {},
      type: [],
      spaces: {},
      menus: {},
      active: false,
      members: {
        [state.auth.uid]: {
          active: true,
          role: "OWNER"
        }
      }
    };

    const newDoc = await firestore.collection("venues").add(venue);

    if (userType === "VENUE") {
      const venueUser = selectors.user(state) as UserVenue;

      const newVenues = venueUser.venues
        ? [...venueUser.venues, newDoc.id]
        : [newDoc.id];

      UpdateUser({ venues: newVenues })(dispatch, getState, null);
    }

    dispatch(createVenueSuccess(newDoc.id, venue));

    return newDoc.id;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(createVenueFailure(error));
  }
};

export const UpdateVenue = (
  venueId: string,
  data: Partial<Venue>
): ThunkAction<Promise<void>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    dispatch(updateVenueInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    await firestore
      .collection("venues")
      .doc(venueId)
      .update(data);

    const venueDoc = await firestore
      .collection("venues")
      .doc(venueId)
      .get();

    const venueData = {
      id: venueDoc.id,
      ...venueDoc.data()
    } as Venue;

    await GetMyVenues()(dispatch, getState, null);

    dispatch(updateVenueSuccess(venueId, venueData));

    return;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(updateVenueFailure(error));
  }
};

export const DeleteVenue = (
  venueId: string
): ThunkAction<
  Promise<string | null>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    dispatch(deleteVenueInit());

    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    await firestore
      .collection("venues")
      .doc(venueId)
      .delete();

    await dispatch(GetMyVenues());

    dispatch(deleteVenueSuccess(venueId));

    return "deleted";
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);
    dispatch(deleteVenueFailure(error));

    return null;
  }
};

export const ActivateVenue = (
  venueId: string
): ThunkAction<
  Promise<boolean | string[]>,
  RootState,
  null,
  Action<string>
> => async (dispatch, getState) => {
  try {
    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const venue = selectors.getVenue(venueId)(state);

    const validation = validateVenue(venue);

    if (!validation.valid) return false;

    await UpdateVenue(venueId, {
      active: true,
      activationTime: moment().unix()
    })(dispatch, getState, null);

    return true;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error);

    return false;
  }
};

export const DeactivateVenue = (
  venueId: string
): ThunkAction<Promise<boolean>, RootState, null, Action<string>> => async (
  dispatch,
  getState
) => {
  try {
    const state = getState();

    if (!state.auth.uid) throw new Error("no authorized user");

    const venue = selectors.getVenue(venueId)(state);
    if (!venue) throw new Error("no venue found");

    await UpdateVenue(venueId, {
      active: false
    })(dispatch, getState, null);

    return true;
  } catch (error) {
    Sentry.captureException(error);

    return false;
  }
};
